import {
  getAccountControllerGetMyAccountsQueryKey,
  useAccountControllerGetMyAccounts,
} from '@safc/api-client';
import { first } from 'es-toolkit/compat';
import {
  createContext,
  useContext,
  useEffect,
} from 'react';

import {
  AccountEntity,
  Action,
} from './permissions/entities.js';
import { AbilityContext } from './permissions/usePermissions.js';
import { useCompanyContext } from './useCompanyContext.js';

type SetSelectedAccount = (accountId?: string) => void;

export const AccountContext = createContext<{
  accountId: string | null;
  setSelectedAccountId: SetSelectedAccount | undefined;
}>({
  accountId: null,
  setSelectedAccountId: undefined,
});

export const useAccountUnitContext = () => {
  const { accountId, setSelectedAccountId } = useContext(AccountContext);
  const { companyId } = useCompanyContext();
  const ability = useContext(AbilityContext);
  const { data, isLoading } = useAccountControllerGetMyAccounts(
    { companyId: companyId ?? undefined },
    {
      query: {
        enabled: companyId !== undefined,
        queryKey: getAccountControllerGetMyAccountsQueryKey({
          companyId: companyId ?? undefined,
        }),
      },
    },
  );

  const holdingsAccounts = (data ?? []).filter(a =>
    ability.can(Action.ViewUnits, new AccountEntity(a.id)),
  );

  const account
    = holdingsAccounts.find(({ id }) => accountId === id)
      ?? first(holdingsAccounts);

  useEffect(() => {
    if (!accountId && holdingsAccounts?.[0]?.id) {
      setSelectedAccountId?.(holdingsAccounts[0].id);
    }
  }, [accountId, holdingsAccounts?.length]);

  useEffect(() => {
    if (holdingsAccounts?.length === 0 && !isLoading) {
      setSelectedAccountId?.(undefined);
    }
  }, []);

  if (!account) {
    return {
      accountId: undefined,
      accounts: undefined,
      setSelectedAccount: undefined,
      isLoading,
    };
  }

  return {
    accountId: account.id,
    setSelectedAccount: setSelectedAccountId,
    account,
    accounts: holdingsAccounts,
    isLoading,
  };
};
